import { dateProvider } from '@utils';
import { Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateWrapper,
  ItemTextWrapper,
  ItemWrapper,
  OptionsWrapper
} from './ExamGroupSelector.styles';

import { Icon } from '@/components/Icon';
import { useExamGroupOptions } from '@/store';

type Props = {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  styles?: React.CSSProperties;
  mode?: 'multiple' | 'tags';
  suffixIcon?: React.ReactNode;
  onExamGroupSelect?: (date: Date | undefined) => void;
  noMargin?: boolean;
};

export const ExamGroupSelector: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  styles,
  mode,
  suffixIcon,
  onExamGroupSelect,
  noMargin
}) => {
  const { t } = useTranslation();
  const { data: examGroups } = useExamGroupOptions();
  const [searchText, setSearchText] = useState('');
  const options = examGroups?.map(c => ({
    label: c.name,
    value: c._id,
    examDate: c.examDate
  }));
  const filteredOptions = searchText
    ? options?.filter(opt => opt.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;
  const handleChange = (val: string | string[], date: Date | undefined) => {
    if (value && Array.isArray(value) && !Array.isArray(val) && value.includes(val)) {
      onChange?.(value.filter(v => v !== val));
      return;
    }
    if (Array.isArray(val)) {
      onChange?.(val);
    } else {
      onChange?.([val, ...(value ?? [])]);
    }
    onExamGroupSelect?.(date);
  };
  return (
    <Select
      mode={mode}
      showSearch
      style={noMargin ? { ...styles, margin: 0 } : styles}
      options={filteredOptions}
      onSearch={setSearchText}
      onChange={val => {
        if (!val) {
          handleChange(val, undefined);
          setSearchText('');
          return;
        }
        const selectedOption = options?.find(opt => opt.value === val);
        handleChange(val, selectedOption?.examDate);
        setSearchText('');
      }}
      value={value}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      placeholder={placeholder ?? t('SELECT_COURSE')}
      suffixIcon={suffixIcon}
      dropdownRender={() => {
        return (
          <OptionsWrapper>
            {filteredOptions?.map(({ label, value: val, examDate }) => {
              const isSelected =
                value && Array.isArray(value) ? value.includes(val) : value === val;
              return (
                <ItemWrapper
                  key={val}
                  $isSelected={isSelected}
                  onClick={() => handleChange(val, examDate)}>
                  <ItemTextWrapper>{label}</ItemTextWrapper>
                  <DateWrapper>
                    {examDate ? dateProvider.format(examDate, '(dd/MM/yyyy)') : undefined}
                  </DateWrapper>
                  {isSelected && <Icon i="Checkmark" color="green7" />}
                </ItemWrapper>
              );
            })}
          </OptionsWrapper>
        );
      }}
    />
  );
};
